import api from '../api/api';
import AdvancedSettingsRequest from '../../types/AdvancedSettingsRequest';
import AdvancedSettingsResponse from '../../types/AdvancedSettingsResponse';
import { PathVisibilityMode } from '../../types/PathVisibilityMode';

class SettingsService {
  static async getAdvancedSettings(
    chatbotId: number,
    signal?: AbortSignal
  ): Promise<AdvancedSettingsResponse> {
    const baseUrl = `chatbot-settings/${chatbotId}/advanced-settings`;
    const apiResponse = await api.get(baseUrl, { signal });
    return apiResponse.data;
  }

  static async updateAdvancedSettings(
    chatbotId: number,
    settings: AdvancedSettingsRequest
  ): Promise<AdvancedSettingsResponse> {
    const baseUrl = `chatbot-settings/${chatbotId}/advanced-settings`;
    const apiResponse = await api.put(baseUrl, settings);
    return apiResponse.data;
  }

  static async updateChatbotName(
    chatbotId: number,
    name: string
  ): Promise<{ chatbotId: number; name: string }> {
    const baseUrl = `chatbot-settings/${chatbotId}/name`;
    const apiResponse = await api.put(baseUrl, { name });
    return apiResponse.data;
  }

  static async updateChatbotEnabled(
    chatbotId: number,
    enabled: boolean
  ): Promise<{ chatbotId: number; enabled: boolean }> {
    const baseUrl = `chatbot-settings/${chatbotId}/enabled`;
    const apiResponse = await api.put(baseUrl, { enabled });
    return apiResponse.data;
  }

  static async updatePathVisibility(
    chatbotId: number,
    mode: PathVisibilityMode,
    paths: string[]
  ): Promise<{ chatbotId: number; mode: PathVisibilityMode; paths: string[] }> {
    const baseUrl = `chatbot-settings/${chatbotId}/path-visibility`;
    const apiResponse = await api.put(baseUrl, { mode, paths });
    return apiResponse.data;
  }

  static async updateRateLimits(
    chatbotId: number,
    rateLimit: number | null,
    rateLimitTotal: number | null
  ): Promise<{
    chatbotId: number;
    rateLimit: number | null;
    rateLimitTotal: number | null;
  }> {
    const baseUrl = `chatbot-settings/${chatbotId}/rate-limit`;
    const apiResponse = await api.put(baseUrl, { rateLimit, rateLimitTotal });
    return apiResponse.data;
  }

  static async getStylingSettings(
    chatbotId: number,
    signal: AbortSignal
  ): Promise<any> {
    const baseUrl = `chatbot-settings/${chatbotId}/styling`;
    const apiResponse = await api.get(baseUrl, { signal });
    return apiResponse.data;
  }

  static async updateStylingSettings(
    chatbotId: number,
    settings: {
      botName: string;
      headerBackground: string;
      headerText: string;
      responseCardBackground: string;
      responseCardText: string;
      userMessageBackground: string;
      userMessageTextMaximiseButtonText: string;
      mainBackground: string;
      mainText: string;
      chatbotMessageBackground: string;
      chatbotMessageText: string;
      inputBackground: string;
      inputText: string;
      ctaLabel: string;
      ctaUrl: string;
      minimisedText: string;
      disclaimerText: string;
      ctaBackground: string;
      ctaText: string;
      disclaimerColour: string;
      hideChatbotImage: boolean;
      titleFontFamily: string;
      bodyFontFamily: string;
      deleteImage?: boolean;
    },
    imageFile?: File | null
  ): Promise<any> {
    const baseUrl = `chatbot-settings/${chatbotId}/styling`;

    const formData = new FormData();
    formData.append(
      'settings',
      new Blob([JSON.stringify(settings)], { type: 'application/json' })
    );
    if (imageFile) {
      formData.append('chatbotHeadImageFile', imageFile);
    }

    const apiResponse = await api.put(baseUrl, formData);
    return apiResponse.data;
  }
}

export default SettingsService;
